/**
 * File Transfer — streaming ZIP wrapper (STORE method only).
 *
 * When the user toggles "Compress folders into ZIP" we wrap a list of Files
 * into a single virtual ZIP file that is read on-demand. Implementation uses
 * STORE (no compression) — for the typical content sent over a P2P link
 * (jpeg/mp4/already-compressed assets) compressed-text savings would be
 * marginal anyway, and STORE keeps the encoder a few dozen lines and avoids
 * pulling in a 14 KB DEFLATE library.
 *
 * Output exposes a Blob-shaped object that supports:
 *   { size, name, type, slice(start, end) → Blob, stream() → ReadableStream }
 *
 * The Sender uses `.size` and `.slice()` so this drop-in replaces a real File.
 */
(function(w){
    'use strict';

    var SIG_LFH  = 0x04034b50;
    var SIG_CDH  = 0x02014b50;
    var SIG_EOCD = 0x06054b50;

    /* CRC32 — polynomial 0xedb88320, table-driven. */
    var CRC_TABLE = (function(){
        var t = new Uint32Array(256);
        for (var i = 0; i < 256; i++){
            var c = i;
            for (var j = 0; j < 8; j++){
                c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
            }
            t[i] = c >>> 0;
        }
        return t;
    })();
    function crc32Bytes(crc, bytes){
        crc = (crc ^ 0xFFFFFFFF) >>> 0;
        for (var i = 0; i < bytes.length; i++){
            crc = (CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8)) >>> 0;
        }
        return (crc ^ 0xFFFFFFFF) >>> 0;
    }
    /* ⚠️⚠️ Промис-цепочка — НЕ уступание. Прежняя версия шла кусками по 1 МБ
       «через промисы» и ВЫГЛЯДЕЛА кооперативной, но .then — микрозадача:
       resolve arrayBuffer() у блоба в памяти не создаёт границы задач, и вся
       цепочка сливалась в ОДИН непрерывный блок. Замерено 07.08: ZIP пакета
       мастеринга = 7.1 с сплошной задачи на ~100 МБ (36% профиля здесь), а в
       поле у Pro-3450 — delay 18 400 мс на клике по mmBatchZip при ~450 МБ
       («зависает подолгу», буквально). Настоящая граница — макрозадача между
       кусками: те же байты, тот же CRC, но страница живая (worst-task = один
       кусок, ~десятки мс). scheduler.yield, где есть, дешевле setTimeout. */
    function _yieldTask(){
        if (typeof scheduler !== 'undefined' && scheduler.yield) return scheduler.yield();
        return new Promise(function(r){ setTimeout(r, 0); });
    }
    function crc32Blob(blob, onBytes){
        // Stream the blob through a CRC accumulator. 1 MB chunks.
        // onBytes(n) — optional: n more bytes were read (the pass over a batch
        // of gigabytes takes a minute or two, and the caller may want to say so).
        var CHUNK = 1024 * 1024;
        var crc = 0;
        var off = 0;
        function next(){
            if (off >= blob.size) return Promise.resolve(crc >>> 0);
            var end = Math.min(blob.size, off + CHUNK);
            return blob.slice(off, end).arrayBuffer().then(function(ab){
                crc = crc32Bytes(crc, new Uint8Array(ab));
                if (onBytes) { try { onBytes(end - off); } catch(_){} }
                off = end;
                return _yieldTask().then(next);
            });
        }
        return next();
    }

    function _utf8(str){ return new TextEncoder().encode(str); }

    function _localFileHeader(nameBytes, crc, size){
        var buf = new ArrayBuffer(30 + nameBytes.length);
        var view = new DataView(buf);
        var bytes = new Uint8Array(buf);
        view.setUint32(0,  SIG_LFH, true);
        view.setUint16(4,  20, true);          // version needed
        view.setUint16(6,  0x0800, true);      // general purpose flag (bit 11 = UTF-8 names)
        view.setUint16(8,  0, true);           // compression = STORE
        view.setUint16(10, 0, true);           // mtime
        view.setUint16(12, 0, true);           // mdate
        view.setUint32(14, crc >>> 0, true);
        view.setUint32(18, size >>> 0, true);  // compressed = uncompressed (STORE)
        view.setUint32(22, size >>> 0, true);
        view.setUint16(26, nameBytes.length, true);
        view.setUint16(28, 0, true);           // extra length
        bytes.set(nameBytes, 30);
        return new Uint8Array(buf);
    }

    function _centralDirHeader(nameBytes, crc, size, lfhOffset){
        var buf = new ArrayBuffer(46 + nameBytes.length);
        var view = new DataView(buf);
        var bytes = new Uint8Array(buf);
        view.setUint32(0,  SIG_CDH, true);
        view.setUint16(4,  0x031E, true);      // version made by (UNIX, 3.0)
        view.setUint16(6,  20, true);          // version needed
        view.setUint16(8,  0x0800, true);      // GP flag
        view.setUint16(10, 0, true);           // method = STORE
        view.setUint16(12, 0, true);           // mtime
        view.setUint16(14, 0, true);           // mdate
        view.setUint32(16, crc >>> 0, true);
        view.setUint32(20, size >>> 0, true);
        view.setUint32(24, size >>> 0, true);
        view.setUint16(28, nameBytes.length, true);
        view.setUint16(30, 0, true);           // extra length
        view.setUint16(32, 0, true);           // comment length
        view.setUint16(34, 0, true);           // disk number
        view.setUint16(36, 0, true);           // internal attrs
        view.setUint32(38, 0, true);           // external attrs
        view.setUint32(42, lfhOffset >>> 0, true);
        bytes.set(nameBytes, 46);
        return new Uint8Array(buf);
    }

    function _eocd(numEntries, cdSize, cdOffset){
        var buf = new ArrayBuffer(22);
        var view = new DataView(buf);
        view.setUint32(0,  SIG_EOCD, true);
        view.setUint16(4,  0, true);           // disk number
        view.setUint16(6,  0, true);           // start disk
        view.setUint16(8,  numEntries, true);
        view.setUint16(10, numEntries, true);
        view.setUint32(12, cdSize >>> 0, true);
        view.setUint32(16, cdOffset >>> 0, true);
        view.setUint16(20, 0, true);           // comment length
        return new Uint8Array(buf);
    }

    /**
     * wrap(files, opts) → Promise<{ size, name, type, slice, stream }>
     *
     * `files` is an array of File-like objects, each with `.path` (forward-slash
     * path inside the zip; defaults to file.name) and `.size`/`.name` from
     * the standard File interface.
     */
    /* What this writer can express. It writes the classic format — 16-bit entry
       count, 32-bit sizes and offsets — and until 2026-10-14 it wrote it for ANY
       input: past either limit the fields simply wrapped round (`>>> 0`,
       setUint16) and the archive came out corrupt without a word. So the limits
       are checked BEFORE the first byte is read, and the answer is a coded
       refusal the caller can act on (batch-deliver.js cuts the batch into parts
       that fit; the file sender falls back to sending the files as they are).
       0xFFFF / 0xFFFFFFFF themselves are the ZIP64 escape values, hence «below». */
    var MAX_ENTRIES = 0xFFFF - 1;
    var MAX_BYTES = 0xFFFFFFFF - 1;
    function _zipTooBig(why, n){
        var e = new Error('ZIP_TOO_BIG: ' + why);
        e.code = 'ZIP_TOO_BIG'; e.why = why; e.value = n;
        return e;
    }
    /** Upper bound of what one entry adds to the archive: local header + name,
     *  the data, central header + name (a UTF-8 name is at most 3 bytes a UTF-16
     *  unit). A caller that cuts a batch into parts counts with the same number
     *  fits() does. */
    function cost(f){
        return 30 + 46 + ((f && f.size) || 0) + 6 * String((f && (f.path || f.name)) || 'file').length;
    }
    /** Does this set of files fit one archive of this writer? → '' or the reason. */
    function fits(files){
        files = files || [];
        if (files.length > MAX_ENTRIES) return 'entries';
        var total = 22;
        for (var i = 0; i < files.length; i++){
            var f = files[i];
            if (((f && f.size) || 0) > MAX_BYTES) return 'entry';
            total += cost(f);
            if (total > MAX_BYTES) return 'size';
        }
        return '';
    }

    function wrap(files, opts){
        opts = opts || {};
        var zipName = opts.name || 'folder.zip';
        var why = fits(files);
        if (why) return Promise.reject(_zipTooBig(why, why === 'entries' ? files.length : 0));

        // Pre-compute headers + offsets so we know the total `size` up front
        // and can `slice()` arbitrary byte ranges without buffering the full
        // archive in memory.
        var meta = [];
        var offset = 0;
        // opts.onProgress(doneBytes, totalBytes) — how far the checksum pass is.
        var totalBytes = 0, doneBytes = 0;
        for (var ti = 0; ti < files.length; ti++) totalBytes += (files[ti] && files[ti].size) || 0;
        var tick = typeof opts.onProgress === 'function' ? function(n){ doneBytes += n; opts.onProgress(doneBytes, totalBytes); } : null;

        var p = Promise.resolve();
        files.forEach(function(f){
            p = p.then(function(){
                var path = (f.path || f.name || 'file').replace(/^[\/\\]+/, '');
                var nameBytes = _utf8(path);
                // A caller that already knows the checksum of an entry says so
                // (`crc32` on the file-like) and the bytes are not read again.
                var known = (typeof f.crc32 === 'number' && f.crc32 >= 0 && f.crc32 <= 0xFFFFFFFF)
                    ? Promise.resolve(f.crc32 >>> 0).then(function(c){ if (tick) { try { tick(f.size || 0); } catch(_){} } return c; })
                    : crc32Blob(f, tick);
                return known.then(function(crc){
                    var lfh = _localFileHeader(nameBytes, crc, f.size);
                    var lfhOffset = offset;
                    offset += lfh.length;
                    var dataStart = offset;
                    offset += f.size;
                    meta.push({
                        file: f,
                        nameBytes: nameBytes,
                        crc: crc,
                        lfh: lfh,
                        lfhOffset: lfhOffset,
                        dataStart: dataStart,
                        dataEnd: dataStart + f.size,
                    });
                });
            });
        });

        return p.then(function(){
            // Build central directory
            var cdParts = meta.map(function(m){ return _centralDirHeader(m.nameBytes, m.crc, m.file.size, m.lfhOffset); });
            var cdSize = cdParts.reduce(function(s, p){ return s + p.length; }, 0);
            var cdOffset = offset;
            offset += cdSize;
            var eocd = _eocd(meta.length, cdSize, cdOffset);
            var totalSize = offset + eocd.length;

            /* ⚠️ Offsets are plain numbers, never `| 0`. The bitwise form made them
               32-bit SIGNED: for an archive of 2 GiB or more, slice(0, size) had a
               negative end and returned an EMPTY Blob — a «saved» archive of zero
               bytes, and a folder sent over the file transfer that stopped making
               sense at the 2 GiB mark. The same negative-index meaning Blob.slice
               gives is kept (from the end), since a caller may rely on it. */
            function clampOffset(v, dflt){
                v = (v == null || v !== v) ? dflt : Math.trunc(+v);
                if (v < 0) v = Math.max(0, totalSize + v);
                return Math.min(totalSize, v);
            }
            function slice(start, end){
                start = clampOffset(start, 0);
                end = clampOffset(end, totalSize);
                if (start >= end) return new Blob([], { type: 'application/zip' });

                var parts = [];
                var cursor = 0;

                function maybeTake(srcLen, takeFn){
                    var srcStart = cursor;
                    var srcEnd = cursor + srcLen;
                    cursor = srcEnd;
                    if (srcEnd <= start || srcStart >= end) return;
                    var s = Math.max(0, start - srcStart);
                    var e = Math.min(srcLen, end - srcStart);
                    if (e > s) takeFn(s, e);
                }

                meta.forEach(function(m){
                    maybeTake(m.lfh.length, function(s, e){
                        parts.push(new Blob([m.lfh.subarray(s, e)]));
                    });
                    maybeTake(m.file.size, function(s, e){
                        parts.push(m.file.slice(s, e));
                    });
                });
                cdParts.forEach(function(cd){
                    maybeTake(cd.length, function(s, e){
                        parts.push(new Blob([cd.subarray(s, e)]));
                    });
                });
                maybeTake(eocd.length, function(s, e){
                    parts.push(new Blob([eocd.subarray(s, e)]));
                });

                return new Blob(parts, { type: 'application/zip' });
            }

            // Public Blob-shaped object that the Sender treats like a File.
            return {
                size: totalSize,
                name: zipName,
                type: 'application/zip',
                lastModified: Date.now(),
                slice: slice,
            };
        });
    }

    /* ── Reading the same format back ─────────────────────────────────────
     *
     * read(blob) → Promise<[{ name, size, method, blob() }]>
     *
     * The reverse door of wrap(): walks the End-Of-Central-Directory record and
     * the central directory, and hands each entry back as a LAZY slice of the
     * source Blob — a STORE entry is `blob.slice(dataStart, dataEnd)`, so a
     * 300 MB package costs no heap to open (this is why the slideshow project
     * package is STORE: the photos are already compressed, and a slice is the
     * only read that stays flat on a 4 GB machine). A DEFLATE entry (a user
     * re-zipped the folder with Explorer / 7-Zip) is inflated through fflate
     * on demand; the format knowledge stays in this one module either way.
     *
     * Not handled, on purpose: ZIP64 (>4 GiB or >65 535 entries — wrap() does
     * not write it either), encrypted entries, spanned archives. Those reject
     * with a coded Error so the caller can say what it saw.
     */
    var FFLATE_SRC_DEFAULT = '/js/vendor/fflate.min.js';
    function _loadFflate(){
        if (w.fflate) return Promise.resolve(w.fflate);
        return new Promise(function(resolve, reject){
            var s = document.createElement('script');
            s.src = (typeof w.__jsSrc === 'function') ? w.__jsSrc(FFLATE_SRC_DEFAULT) : FFLATE_SRC_DEFAULT;
            s.onload = function(){ w.fflate ? resolve(w.fflate) : reject(new Error('FFLATE_MISSING')); };
            s.onerror = function(){ reject(new Error('FFLATE_LOAD_FAILED')); };
            document.head.appendChild(s);
        });
    }
    function _zipErr(code, msg){ var e = new Error(msg || code); e.code = code; return e; }

    function read(blob){
        if (!blob || typeof blob.slice !== 'function' || typeof blob.size !== 'number') {
            return Promise.reject(_zipErr('ZIP_NOT_A_BLOB'));
        }
        if (blob.size < 22) return Promise.reject(_zipErr('ZIP_TRUNCATED'));
        // EOCD sits in the last 22 bytes + up to 65 535 bytes of comment.
        var tailLen = Math.min(blob.size, 22 + 65535);
        return blob.slice(blob.size - tailLen, blob.size).arrayBuffer().then(function(ab){
            var tail = new Uint8Array(ab);
            var dv = new DataView(ab);
            var eocdAt = -1;
            for (var i = tail.length - 22; i >= 0; i--) {
                if (dv.getUint32(i, true) === SIG_EOCD) { eocdAt = i; break; }
            }
            if (eocdAt < 0) throw _zipErr('ZIP_NO_EOCD', 'not a zip archive');
            var count = dv.getUint16(eocdAt + 10, true);
            var cdSize = dv.getUint32(eocdAt + 12, true);
            var cdOffset = dv.getUint32(eocdAt + 16, true);
            if (count === 0xFFFF || cdSize === 0xFFFFFFFF || cdOffset === 0xFFFFFFFF) throw _zipErr('ZIP64_UNSUPPORTED');
            if (cdOffset + cdSize > blob.size) throw _zipErr('ZIP_TRUNCATED');
            return blob.slice(cdOffset, cdOffset + cdSize).arrayBuffer().then(function(cdab){
                var cd = new DataView(cdab);
                var bytes = new Uint8Array(cdab);
                var dec = new TextDecoder('utf-8');
                var entries = [];
                var p = 0;
                for (var n = 0; n < count; n++) {
                    if (p + 46 > bytes.length || cd.getUint32(p, true) !== SIG_CDH) throw _zipErr('ZIP_BAD_CDIR');
                    var flag = cd.getUint16(p + 8, true);
                    var method = cd.getUint16(p + 10, true);
                    var crc = cd.getUint32(p + 16, true);
                    var csize = cd.getUint32(p + 20, true);
                    var usize = cd.getUint32(p + 24, true);
                    var nLen = cd.getUint16(p + 28, true);
                    var xLen = cd.getUint16(p + 30, true);
                    var cLen = cd.getUint16(p + 32, true);
                    var lfhOffset = cd.getUint32(p + 42, true);
                    var name = dec.decode(bytes.subarray(p + 46, p + 46 + nLen));
                    p += 46 + nLen + xLen + cLen;
                    if (flag & 0x1) throw _zipErr('ZIP_ENCRYPTED');
                    if (name.charAt(name.length - 1) === '/') continue;   // directory placeholder
                    entries.push(_entry(blob, { name: name, size: usize, csize: csize, method: method, crc: crc, lfhOffset: lfhOffset }));
                }
                return entries;
            });
        });
    }

    function _entry(blob, m){
        var dataStartP = null;
        function dataStart(){
            if (dataStartP) return dataStartP;
            // The local header repeats name/extra with ITS OWN lengths (they may
            // differ from the central copy — zip tools do that), so the data
            // offset must be read from the local header, never computed from
            // the central one.
            dataStartP = blob.slice(m.lfhOffset, m.lfhOffset + 30).arrayBuffer().then(function(ab){
                var dv = new DataView(ab);
                if (dv.getUint32(0, true) !== SIG_LFH) throw _zipErr('ZIP_BAD_LOCAL_HEADER');
                var nLen = dv.getUint16(26, true), xLen = dv.getUint16(28, true);
                var start = m.lfhOffset + 30 + nLen + xLen;
                if (start + m.csize > blob.size) throw _zipErr('ZIP_TRUNCATED');
                return start;
            });
            return dataStartP;
        }
        return {
            name: m.name,
            size: m.size,
            method: m.method,
            crc: m.crc,
            /** → Promise<Blob>: a lazy slice for STORE, an inflated Blob for DEFLATE. */
            blob: function(type){
                return dataStart().then(function(start){
                    var raw = blob.slice(start, start + m.csize, type || '');
                    if (m.method === 0) return raw;
                    if (m.method !== 8) throw _zipErr('ZIP_METHOD_UNSUPPORTED', 'method ' + m.method);
                    return _loadFflate().then(function(fflate){
                        return raw.arrayBuffer().then(function(ab){
                            var out = fflate.inflateSync(new Uint8Array(ab));
                            return new Blob([out], { type: type || '' });
                        });
                    });
                });
            },
        };
    }

    w.__FileTransfer = w.__FileTransfer || {};
    w.__FileTransfer.FolderZip = { wrap: wrap, read: read, crc32Blob: crc32Blob, fits: fits, cost: cost, MAX_ENTRIES: MAX_ENTRIES, MAX_BYTES: MAX_BYTES };
})(window);
